import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SettingsService } from '@app/services/settings/settings.service';
import { AuditLogsDialogComponent } from '@app/storybook/layouts/components/header/components/audit-logs-dialog/audit-logs-dialog.component';
import { NewerSave } from './change-review.model';
import { rebaseOnLatestSave } from './change-review.utils';

interface SavedEntity {
  updatedAt?: string | Date;
}

export interface StaleSaveCheck<T> {
  // e.g. 'This method'
  entityName: string;
  opened: SavedEntity;
  savedPayload: T;
  loadLatest: () => Observable<SavedEntity>;
  // audit log section and location of this entity, e.g. 'Checkout Group' / 'DeliveryMethod:42'
  auditLog: { where: string; location: string };
  entityPaths?: Record<string, string>;
  isSame?: (path: string, a: unknown, b: unknown) => boolean;
}

@Injectable({ providedIn: 'root' })
export class StaleSaveService {
  constructor(
    private dialog: MatDialog,
    private settingsService: SettingsService,
  ) {}

  checkForNewerSave<T extends object>(check: StaleSaveCheck<T>): Observable<NewerSave<T> | null> {
    if (!check.opened?.updatedAt) return of(null);

    return check.loadLatest().pipe(
      switchMap((latest) => {
        const isNewer =
          latest?.updatedAt && new Date(latest.updatedAt).getTime() !== new Date(check.opened.updatedAt).getTime();
        if (!isNewer) return of(null);

        return this.findLastEditor(check.auditLog).pipe(
          map((editor) => ({
            baseline: rebaseOnLatestSave(check.savedPayload, check.opened, latest, check.entityPaths, check.isSame),
            banner: {
              title: `${check.entityName} changed while you were editing`,
              message: `${editor ?? 'Someone'} saved changes at ${new Date(latest.updatedAt).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
              })}. Saving now replaces them.`,
            },
          })),
        );
      }),
      catchError(() => of(null)),
    );
  }

  openAuditLogs(): void {
    this.dialog.open(AuditLogsDialogComponent, {
      backdropClass: 'global_modal_overlay_subdued',
      panelClass: 'overflow-visible-class',
      width: '85vw',
      data: {},
    });
  }

  // audit history only filters by section, so we scan the latest page
  private findLastEditor({ where, location }: StaleSaveCheck<object>['auditLog']): Observable<string | null> {
    return this.settingsService
      .getAuditLogs({ orderBy: 'timestamp', orderDirection: 'DESC', page: 0, recordsPerPage: 25, where })
      .pipe(
        map(
          (response: { results?: { userEmail?: string; location?: string }[] }) =>
            response?.results?.find((log) => (log.location ?? '').split(' ').includes(location))?.userEmail ?? null,
        ),
        catchError(() => of(null)),
      );
  }
}
